// src/pages/Home.tsx
import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';

import Header from '../components/Header';
import CategoryList from '../components/CategoryList';
import BrandList from '../components/BrandList';
import ProductCard from '../components/ProductCard'; // Assuming ProductCard accepts new props
import { fetchCategories, fetchProducts, fetchBrands, fetchAllMtPrices, fetchCustomer } from '../utils/api'; // Import fetchCustomer
import { Category, Product, Brand, Customer } from '../utils/types'; // Import Customer type
import Pagination from '../components/Pagination';
import { Tooltip as ReactTooltip } from "react-tooltip";
import { FaBars } from 'react-icons/fa';
import { checkSession } from '../utils/api';
import { hasRole } from '../utils/auth';
import { useConfig } from '../context/ConfigContext';
// --- Import Context Hooks ---
import { useAuth } from '../context/AuthContext';
import { useCustomerContext } from '../context/customerContext';

// Define the structure returned by fetchAllMtPrices (adjust if needed)
interface FetchedMTPrice {
    id: number; // Product ID
    price: number;
    // Add other fields if returned, like product_name
}

// Define the structure we'll use in state (Map for efficient lookup)
// The value includes the price and the role it applies to
type MTPriceMap = Map<number, { price: number; role: string }>;

const Home = () => {
    const [categories, setCategories] = useState<Category[]>([]);
    const [brands, setBrands] = useState<Brand[]>([]);
    const [products, setProducts] = useState<Product[]>([]);
    const [loadingProducts, setLoadingProducts] = useState(true);
    const [errorProducts, setErrorProducts] = useState<string | null>(null);
    const [sort, setSort] = useState('updated_at DESC');
    const [perPage] = useState(30);
    const [selectedCategories, setSelectedCategories] = useState<Category[]>([]);
    const [selectedBrands, setSelectedBrands] = useState<Brand[]>([]);
    const [totalPages, setTotalPages] = useState(1);
    const [totalProducts, setTotalProducts] = useState(0);
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
    const [isBrandListVisible, setIsBrandListVisible] = useState(false);
    const [isCategoryListVisible, setIsCategoryListVisible] = useState(false);
    const brandMenuRef = useRef<HTMLDivElement>(null);
    const categoryMenuRef = useRef<HTMLDivElement>(null);

    // --- State for MT Prices ---
    const [mtPrices, setMtPrices] = useState<MTPriceMap>(new Map());
    const [loadingPrices, setLoadingPrices] = useState(false);
    const [errorPrices, setErrorPrices] = useState<string | null>(null);

    // --- Get User and Customer Context ---
    const { user: loggedInUser } = useAuth();
    const { selectedSessionCustomer } = useCustomerContext();
    const { settings } = useConfig(); // Get app settings if needed

    // --- Determine Effective User (for pricing and actions) ---
    const effectiveUser = useMemo(() => {
        const isAdminOrStaff = hasRole(['super_admin', 'staff', 'technician']);
        const isAdminActingAs = isAdminOrStaff && selectedSessionCustomer;

        if (isAdminActingAs) {
            // console.log("Admin acting as customer:", selectedSessionCustomer);
            const customerRole = selectedSessionCustomer?.role || 'dealer';

            return {
                id: selectedSessionCustomer.user_id,
                role: customerRole,
                isAuthenticated: true,
                customer_id: selectedSessionCustomer.id
            };
        } else {
            let loggedInUserRole: string | undefined;
            let loggedInCustomerId: number | undefined;
            try {
                const rolesPermissionsItem = localStorage.getItem('rolesPermissions');
                if (rolesPermissionsItem) {
                    const rolesPermissions = JSON.parse(rolesPermissionsItem);
                    loggedInUserRole = rolesPermissions?.roles?.[0];
                }
                // --- Get customer_id from localStorage for logged-in user ---
                const storedCustomerId = localStorage.getItem('customer');
                if (storedCustomerId) {
                    // Assuming it's stored as just the ID number after JSON.parse
                    const parsedId = JSON.parse(storedCustomerId);
                    if (typeof parsedId === 'number') {
                        loggedInCustomerId = parsedId;
                    } else if (typeof parsedId === 'object' && parsedId !== null && typeof parsedId.id === 'number') {
                        // Handle case where it might be stored as { id: number }
                        loggedInCustomerId = parsedId.id;
                    }
                }
            } catch (e) {
                console.error("Failed to parse rolesPermissions or customer ID from localStorage in Home:", e);
            }

            return {
                id: loggedInUser?.id,
                role: loggedInUserRole,
                isAuthenticated: !!loggedInUser,
                customer_id: loggedInCustomerId // Use the ID retrieved from localStorage
            };
        }
    }, [loggedInUser, selectedSessionCustomer]);
    // --- End Determine Effective User ---

    // --- State to hold the full customer data for the effective user ---
    const [effectiveCustomerData, setEffectiveCustomerData] = useState<Customer | null>(null);
    const [loadingCustomerData, setLoadingCustomerData] = useState(false);
    const [errorCustomerData, setErrorCustomerData] = useState<string | null>(null);

    // --- Fetch Effective Customer Data ---
    useEffect(() => {
        const loadEffectiveCustomer = async () => {
            // Only fetch if we have an authenticated user with a customer_id
            if (effectiveUser.isAuthenticated && effectiveUser.customer_id) {
                setLoadingCustomerData(true);
                setErrorCustomerData(null);
                try {
                    // console.log(`Fetching customer data for ID: ${effectiveUser.customer_id}`); // Debug log
                    const customerData = await fetchCustomer(effectiveUser.customer_id);
                    setEffectiveCustomerData(customerData);
                    // console.log("Effective customer data loaded:", customerData); // Debug log
                } catch (err: any) {
                    // console.error("Failed to load effective customer data:", err);
                    setErrorCustomerData(err.message || 'Failed to load customer details.');
                    setEffectiveCustomerData(null); // Clear on error
                } finally {
                    setLoadingCustomerData(false);
                }
            } else {
                // If not authenticated or no customer_id, clear the data
                setEffectiveCustomerData(null);
                setLoadingCustomerData(false);
                setErrorCustomerData(null);
                if (effectiveUser.isAuthenticated && !effectiveUser.customer_id) {
                    console.warn("Authenticated user has no associated customer_id."); // Debug log
                }
            }
        };

        loadEffectiveCustomer();
    }, [effectiveUser.isAuthenticated, effectiveUser.customer_id]); // Re-run if auth status or customer_id changes


    // --- Debounced Search Handler ---
    const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }
        debounceTimeout.current = setTimeout(() => {
            setSearch(value); // Update search state after debounce
            setCurrentPage(1); // Reset to first page on new search
        }, 300); // 300ms debounce time
    }, []);

    // --- Click Outside Handlers for Menus ---
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (isBrandListVisible && brandMenuRef.current && !brandMenuRef.current.contains(event.target as Node)) {
                setIsBrandListVisible(false);
            }

            if (isCategoryListVisible && categoryMenuRef.current && !categoryMenuRef.current.contains(event.target as Node)) {
                setIsCategoryListVisible(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isBrandListVisible, isCategoryListVisible]);

    // --- Fetch Initial Categories & Brands ---
    useEffect(() => {
        const loadInitialData = async () => {
            try {
                const [brandData, categoryData] = await Promise.all([fetchBrands(), fetchCategories()]);

                // console.log('Initial brands and categories loaded:', brandData, categoryData);

                setBrands(brandData);
                setCategories(categoryData);
            } catch (err) {
                console.error('Failed to load initial brands/categories:', err);
                // Optionally set an error state here
            }
        };
        loadInitialData();
    }, []); // Run only once on mount

    // --- Fetch Products ---
    useEffect(() => {
        const loadProducts = async () => {
            setLoadingProducts(true);
            setErrorProducts(null);
            setErrorPrices(null); // Clear price error when fetching new products
            setMtPrices(new Map()); // Clear old prices
            try {
                const categoryIds = selectedCategories.map(c => c.id);
                const brandIds = selectedBrands.map(b => b.id);
                const { products: fetchedProducts, total_pages, total_products } = await fetchProducts(
                    currentPage,
                    search,
                    categoryIds.length > 0 ? categoryIds : null,
                    brandIds.length > 0 ? brandIds : null,
                    perPage,
                    sort
                );

                // console.log('categoryIds >>', categoryIds);
                // console.log('fetchedProducts >>', fetchedProducts);

                setProducts(fetchedProducts);
                setTotalPages(total_pages);
                setTotalProducts(total_products);

            } catch (err: any) {
                setErrorProducts(err.message || 'Failed to load products.');
                setProducts([]); // Clear products on error
            } finally {
                setLoadingProducts(false);
            }
        };
        loadProducts();
    }, [currentPage, search, selectedCategories, selectedBrands, perPage, sort]);

    // --- Fetch MT Prices (depends on products and effectiveUser) ---
    useEffect(() => {
        // Only fetch if products are loaded, user is authenticated, and has ID/Role
        if (products.length > 0 && effectiveUser.isAuthenticated && effectiveUser.id && effectiveUser.role) {
            const productIds = products.map(p => p.id);

            const loadPrices = async () => {
                setLoadingPrices(true);
                setErrorPrices(null);
                try {
                    // Call API with product IDs and effective user details
                    const priceDataArray: FetchedMTPrice[] = await fetchAllMtPrices(
                        productIds,
                        effectiveUser.id,
                        effectiveUser.role
                    );

                    // console.log("fetchAllMtPrices MT prices:", priceDataArray, effectiveUser.role); // Debug log

                    // Convert array to Map for easy lookup in ProductCard
                    const priceMap: MTPriceMap = new Map();
                    priceDataArray.forEach(item => {
                        // Store price and the role it applies to
                        priceMap.set(item.id, { price: item.price, role: effectiveUser.role! });
                    });
                    setMtPrices(priceMap);

                } catch (err: any) {
                    console.error("Failed to load MT prices:", err);
                    setErrorPrices(err.message || 'Failed to load prices.');
                    setMtPrices(new Map()); // Clear prices on error
                } finally {
                    setLoadingPrices(false);
                }
            };
            loadPrices();
        } else {
            // Clear prices if no products or no valid user/role
            setMtPrices(new Map());
            setLoadingPrices(false); // Ensure loading is false
            setErrorPrices(null); // Ensure error is null
        }
    }, [products, effectiveUser]); // Re-run when products list or effective user changes

    // --- Session Check Interval (Keep as is) ---
    useEffect(() => {
        const userStr = localStorage.getItem('user');
        const user = userStr && userStr !== "undefined" ? JSON.parse(userStr) : {};
        if (user && user.remember_token && user.session_id && !hasRole(['super_admin', 'staff', 'technician'])) {
            const interval = setInterval(() => {
                checkSession();
            }, 5 * 60 * 1000); // Check every 5 minutes for non-admins
            return () => clearInterval(interval);
        }
    }, []);


    // --- Helper Functions ---
    const getAllDescendantCategories = useCallback((parentCategory: Category, allCategories: Category[]): Category[] => {
        const children = allCategories.filter(cat => Number(cat.cat_parent) === parentCategory.id);
        let descendants: Category[] = [...children];
        children.forEach(child => {
            descendants = descendants.concat(getAllDescendantCategories(child, allCategories));
        });
        return descendants;
    }, []);

    const handleCategorySelect = useCallback((category: Category) => {
        const descendants = getAllDescendantCategories(category, categories);
        const categoriesToAdd = [category, ...descendants];

        setSelectedCategories(prev => {
            const newCategories = [...prev];
            categoriesToAdd.forEach(catToAdd => {
                if (!newCategories.some(c => c.id === catToAdd.id)) {
                    newCategories.push(catToAdd);
                }
            });
            return newCategories;
        });
        setCurrentPage(1);
        setIsCategoryListVisible(false);
    }, [categories, getAllDescendantCategories]);

    const handleCategoryRemove = useCallback((categoryToRemove: Category) => {
        const descendantsToRemove = getAllDescendantCategories(categoryToRemove, categories);
        const idsToRemove = new Set([categoryToRemove.id, ...descendantsToRemove.map(c => c.id)]);
        setSelectedCategories(prev => prev.filter(c => !idsToRemove.has(c.id)));
        setCurrentPage(1); // Reset page
    }, [categories, getAllDescendantCategories]);

    // Inside Home.tsx, perhaps near getAllDescendantCategories
    const hasDirectChildren = useCallback((categoryId: number, allCategories: Category[]): boolean => {
        return allCategories.some(cat => Number(cat.cat_parent) === categoryId);
    }, []);

    const handleBrandSelect = useCallback((brand: Brand) => {
        setSelectedBrands(prev => {
            if (prev.some(b => b.id === brand.id)) {
                return prev; // Already selected
            }
            return [...prev, brand];
        });
        setCurrentPage(1);
        setIsBrandListVisible(false);
    }, []);

    const handleBrandRemove = useCallback((brandToRemove: Brand) => {
        setSelectedBrands(prev => prev.filter(b => b.id !== brandToRemove.id));
        setCurrentPage(1);
    }, []);

    // --- Render Logic ---
    return (
        <div>
            <Helmet>
                <title>{settings?.remark || 'Home'}</title>
                {/* Add other meta tags */}
            </Helmet>

            <Header />

            {/* Search, Sort, Filters */}
            <div className="sticky top-11 z-20 
                                    bg-neutral-300/20 hover:bg-neutral-300/30  backdrop-blur-[1px] border border-neutral-400/20
            flex flex-col md:flex-row mt-1 mb-0 px-2 py-1 border-b">
                {/* Search Input */}
                <div className='text-md w-full md:w-3/5 lg:w-4/6 mb-1 md:mb-0 md:pr-2'>
                    <form onSubmit={(e) => e.preventDefault()}>
                        <input
                            type="search"
                            name="search"
                            placeholder="Search products..."
                            onChange={handleSearchChange}
                            className="bg-blue-50 border border-blue-300 px-2 py-2 w-full rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                    </form>
                </div>


                {/* Sort Options */}
                <div className='text-sm z-20 w-full md:w-2/5 lg:w-2/6 '>
                    <select
                        name="sort"
                        onChange={(e) => { setSort(e.target.value); setCurrentPage(1); }}
                        value={sort}
                        className='w-full h-8  rounded px-2 
                        
                        bg-white-300/90 hover:bg-neutral-300/80  backdrop-blur-[1px] border border-neutral-400/20

                        py-1 focus:outline-none focus:ring-1 focus:ring-blue-500'
                    >
                        <option value="updated_at DESC">Sort: Newest</option>
                        <option value="updated_at ASC">Sort: Oldest</option>
                        <option value="product_value ASC">Sort: Price Low-High</option>
                        <option value="product_value DESC">Sort: Price High-Low</option>
                        <option value="product_name ASC">Sort: Name A-Z</option>
                        <option value="product_name DESC">Sort: Name Z-A</option>
                    </select>
                </div>

            </div>

            {/* Category/Brand Toggles & Menus */}
            <div className="sticky top-32 md:top-24  z-20 flex flex-row md:flex-row justify-end items-end  mb-4 px-2"> {/* Container for buttons */}


                <div className='text-sm z-20 w-full md:w-2/5 lg:w-2/6 '>

                    <button
                        className="w-2/5 relative inline-flex items-center mr-1 p-2 bg-orange-100 text-xs text-orange-700 rounded hover:bg-orange-200 focus:outline-none focus:ring-1 focus:ring-orange-400"
                        onClick={() => setIsCategoryListVisible(!isCategoryListVisible)}
                    >
                        <FaBars size={14} className="mr-1" />
                        <span>Category</span>
                    </button>

                    <button
                        className="w-2/5 relative inline-flex items-center mr-1 p-2 bg-purple-100 text-xs text-purple-700 rounded hover:bg-purple-200 focus:outline-none focus:ring-1 focus:ring-purple-400"
                        onClick={() => setIsBrandListVisible(!isBrandListVisible)}
                    >
                        <FaBars size={14} className="mr-1" />
                        <span>Brand</span>
                    </button>

                </div>
                {/* Category Menu */}
                <div ref={categoryMenuRef} className={`absolute top-full left-0 mt-1 z-30 w-full max-w-xs  md:max-w-md bg-orange-50 border border-orange-200 rounded shadow-lg category-menu ${isCategoryListVisible ? 'block' : 'hidden'}`}>
                    {isCategoryListVisible && <CategoryList categories={categories} onSelectCategory={handleCategorySelect} selectedCategories={selectedCategories} />}
                </div>

                {/* Brand Menu - Position relative to its button or main container */}
                <div ref={brandMenuRef} className={`absolute top-full left-0 mt-1 z-30 w-full max-w-xs md:max-w-md bg-purple-50 border border-purple-200 rounded shadow-lg brand-menu ${isBrandListVisible ? 'block' : 'hidden'}`}>
                    {isBrandListVisible && <BrandList brands={brands} onSelectBrand={handleBrandSelect} selectedBrands={selectedBrands} />}
                </div>
            </div>


            {/* Main Content Area */}
            <section className="products-section px-2">

                {/* Selected Filters Display & Reset */}
                <div className='mb-4 flex flex-wrap items-center gap-2'>
                    <span className='text-sm font-medium italic'>Filters:</span>

                    <div className="flex flex-wrap items-center gap-1">
                        <span className="text-xs mr-1 font-bold">Category:</span>
                        {selectedCategories.length === 0 ? (
                            <span className="px-2 py-1 text-xs rounded-lg shadow bg-gray-100 text-gray-500 font-semibold">All</span>
                        ) : (
                            selectedCategories.map(category => {
                                const isParentCategory = hasDirectChildren(category.id, categories);
                                return (
                                    <span
                                        key={category.id}
                                        className={`inline-flex items-center px-2 py-1 rounded-lg shadow 
                                            ${isParentCategory ? 'bg-blue-100 text-blue-800 border border-blue-300' : 'bg-orange-100 text-orange-800'}`}
                                    >
                                        <span className="font-semibold">{category.cat_name}
                                            {category.cat_prod_qty > 0 && (
                                                <span className='text-xs text-gray-400'>({category.cat_prod_qty})</span>
                                            )}
                                        </span>
                                        <button onClick={() => handleCategoryRemove(category)} className="ml-2 text-red-500 cursor-pointer hover:text-red-700" data-tooltip-id={`removeCatTooltip-${category.id}`} data-tooltip-content='Remove Category Filter'>
                                            &times;
                                        </button>
                                        <ReactTooltip id={`removeCatTooltip-${category.id}`} place="bottom" style={{ backgroundColor: "rgb(254 243 199)", color: "rgb(180 83 9)", fontSize: '0.75rem', zIndex: 50 }} />
                                    </span>
                                );
                            })
                        )}
                    </div>


                    <div className="flex flex-wrap items-center gap-1">
                        <span className="text-xs mr-1 font-bold">Brand:</span>
                        {selectedBrands.length === 0 ? (
                            <span className="px-2 py-1 text-xs rounded-lg shadow bg-gray-100 text-gray-500 font-semibold">All</span>
                        ) : (
                            selectedBrands.map(brand => (
                                <span key={brand.id} className="inline-flex items-center px-2 py-1 text-xs rounded-lg shadow bg-purple-100 text-purple-800">
                                    <span className="font-semibold">{brand.cat_name}
                                        {brand.brand_prod_qty > 0 && (
                                            <span className="text-xs text-gray-400">({brand.brand_prod_qty})</span>
                                        )}
                                    </span>
                                    <button onClick={() => handleBrandRemove(brand)} className="ml-2 text-red-500 cursor-pointer hover:text-red-700" data-tooltip-id={`removeBrandTooltip-${brand.id}`} data-tooltip-content='Remove Brand Filter'>
                                        &times;
                                    </button>
                                    <ReactTooltip id={`removeBrandTooltip-${brand.id}`} place="bottom" style={{ backgroundColor: "rgb(254 243 199)", color: "rgb(180 83 9)", fontSize: '0.75rem', zIndex: 50 }} />
                                </span>
                            ))
                        )}
                    </div>

                    {/* Total Products Count */}
                    {!loadingProducts && (
                        <span className='ml-auto mr-1 text-sm md:text-lg font-medium text-red-600'>
                            {totalProducts} Products Found
                        </span>
                    )}
                </div>

                {/* Loading/Error States */}
                {loadingProducts && (
                    <div className="text-center py-10"> {/* Changed p to div */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                            {Array.from({ length: 8 }).map((_, n) => (
                                <div className="animate-pulse space-y-4" key={n}>
                                    <div className="h-48 bg-gray-200 rounded-xl"></div>
                                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                                </div>
                            ))}
                        </div>
                        <p className="mt-4">Loading products...</p> {/* Wrapped text in its own p tag */}
                    </div>

                )}
                {errorProducts && <p className="text-center py-10 text-red-500">{errorProducts}</p>}
                {/* Optional: Show price loading/error separately */}
                {loadingPrices && !loadingProducts && <p className="text-center text-sm text-gray-500">Loading prices...</p>}
                {errorPrices && !loadingProducts && <p className="text-center text-sm text-red-500">{errorPrices}</p>}
                {/* Loading/Error States for Customer Data */}
                {loadingCustomerData && <p className="text-center text-sm text-gray-500">Loading customer details...</p>}
                {errorCustomerData && <p className="text-center text-sm text-red-500">{errorCustomerData}</p>}


                {/* Product Grid */}
                <div className="flex flex-wrap -mx-1 pb-16">
                    {!loadingProducts && products.length > 0 ? (
                        products.map((product) => (
                            <ProductCard
                                key={product.id}
                                product={product}
                                mtPriceData={mtPrices.get(product.id)}
                                effectiveUserId={effectiveUser.id}
                                effectiveUserRole={effectiveUser.role}
                                effectiveUserIsAuthenticated={effectiveUser.isAuthenticated}
                                // --- Pass the fetched customer data ---
                                customerForAction={effectiveCustomerData}
                                // --- Pass loading state ---
                                isLoadingCustomerForAction={loadingCustomerData}
                                // --- Add this prop ---
                                loadingPrices={loadingPrices}
                            // --- End add prop ---
                            />
                        ))
                    ) : (
                        // Show message only if not loading and filters/search might be active
                        !loadingProducts && (selectedCategories.length > 0 || selectedBrands.length > 0 || search) && (
                            <div className='w-full text-center py-10'>
                                <p className='text-gray-600'>No products found matching your criteria.</p>
                            </div>
                        )
                    )}
                    {/* Show message if not loading and no products at all */}
                    {!loadingProducts && products.length === 0 && !search && selectedCategories.length === 0 && selectedBrands.length === 0 && (
                        <div className='w-full text-center py-10'>
                            <p className='text-gray-600'>No products available.</p>
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {!loadingProducts && totalPages > 1 && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                    />
                )}
            </section>
        </div>
    );
};

export default Home;
